import React, { useState, useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { motion } from 'framer-motion';
import { 
  Palette, 
  Sparkles, 
  Layers, 
  Compass, 
  MapPin, 
  Phone, 
  Mail, 
  Send, 
  CheckCircle2, 
  Award
} from 'lucide-react';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import jeetimage from "../Images/jeet_image.jpeg";
import { db } from '../firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';

const AboutUs = () => {
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic'
    });
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addDoc(collection(db, 'contacts'), {
        name: formData.name,
        email: formData.email,
        message: formData.message,
        createdAt: Timestamp.now()
      });
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      console.error('Error submitting form:', err);
    } finally {
      setLoading(false);
    }
  };

  const values = [
    {
      icon: <Palette className="w-6 h-6 text-yellow-600" />,
      title: "Artistic Vision",
      description: "Original designs and hand-painted masterworks crafted with authentic expression."
    },
    {
      icon: <Layers className="w-6 h-6 text-yellow-600" />,
      title: "Premium Materials",
      description: "Archival grade papers, canvas textures, and rich pigments engineered to endure."
    },
    {
      icon: <Sparkles className="w-6 h-6 text-yellow-600" />,
      title: "Bespoke Customization",
      description: "Tailored sizing, colors, and compositions harmonized to your exact architectural space."
    },
    {
      icon: <Compass className="w-6 h-6 text-yellow-600" />,
      title: "Design Collaboration",
      description: "Close partnership with architects, interior designers, and discerning homeowners."
    }
  ];

  return (
    <div className="bg-[#f8f9fa] min-h-screen font-serif text-gray-800 selection:bg-yellow-100 selection:text-yellow-900">
      <Navbar />

      {/* 1. Hero Section */}
      <section className="relative pt-28 sm:pt-32 pb-14 md:pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Subtle decorative background gradient */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-gradient-to-b from-yellow-50/50 via-gray-100/40 to-transparent -z-10 pointer-events-none rounded-full blur-3xl opacity-80" />

        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs sm:text-sm font-sans font-semibold tracking-wider text-yellow-800 bg-yellow-100/80 border border-yellow-200/70 uppercase mb-5 shadow-xs">
              <Sparkles className="w-3.5 h-3.5 text-yellow-600" /> Our Story & Philosophy
            </span>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-8 tracking-tight">
              Every Space Deserves
              <span className="block mt-2 sm:mt-3.5 text-yellow-600 italic font-serif">
                To Tell Your Story
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto font-sans font-light">
              At <strong className="text-gray-900 font-semibold font-serif">VIZAURA</strong>, we transform interiors with expressive and intentional artworks. From exclusive hand paintings to designer wallcoverings and custom art prints, our curated collections elevate homes, boutique offices, and luxury commercial spaces.
            </p>
          </motion.div>

          {/* Quick Stats Banner */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-12 pt-8 border-t border-gray-200/80 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="p-4 rounded-xl bg-white border border-gray-200/70 shadow-xs text-center">
              <span className="block text-2xl sm:text-3xl font-bold text-gray-900 font-sans">100%</span>
              <span className="text-xs sm:text-sm text-gray-500 font-sans">Original Artistry</span>
            </div>
            <div className="p-4 rounded-xl bg-white border border-gray-200/70 shadow-xs text-center">
              <span className="block text-2xl sm:text-3xl font-bold text-gray-900 font-sans">1000+</span>
              <span className="text-xs sm:text-sm text-gray-500 font-sans">Curated Designs</span>
            </div>
            <div className="p-4 rounded-xl bg-white border border-gray-200/70 shadow-xs text-center">
              <span className="block text-2xl sm:text-3xl font-bold text-gray-900 font-sans">Bespoke</span>
              <span className="text-xs sm:text-sm text-gray-500 font-sans">Custom Sizing</span>
            </div>
            <div className="p-4 rounded-xl bg-white border border-gray-200/70 shadow-xs text-center">
              <span className="block text-2xl sm:text-3xl font-bold text-gray-900 font-sans">Pan-India</span>
              <span className="text-xs sm:text-sm text-gray-500 font-sans">Delivery & Support</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Founder / Creative Direction Section */}
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-3xl p-6 sm:p-10 md:p-14 shadow-xl shadow-gray-200/60 border border-gray-200/80 flex flex-col md:flex-row items-center gap-8 md:gap-14">
            
            {/* Founder Image Frame */}
            <div className="w-full md:w-5/12 flex justify-center" data-aos="fade-right">
              <div className="relative group max-w-[320px] sm:max-w-[360px] w-full">
                {/* Background decorative frame */}
                <div className="absolute -inset-2 rounded-2xl bg-gradient-to-tr from-yellow-200/40 via-yellow-100/20 to-gray-200/30 blur-sm group-hover:blur-md transition-all duration-300" />
                
                <div className="relative rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 shadow-md">
                  <img
                    src={jeetimage}
                    alt="Jeet Mistry - Founder of VIZAURA"
                    className="w-full h-[360px] sm:h-[400px] object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent opacity-80" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="text-xl font-bold font-serif leading-tight">Jeet Mistry</p>
                    <p className="text-xs sm:text-sm text-yellow-300 font-sans tracking-wide">Founder & Creative Director</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Founder Bio Text */}
            <div className="w-full md:w-7/12 text-center md:text-left space-y-5" data-aos="fade-left">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-yellow-800 bg-yellow-50 px-3 py-1 rounded-md border border-yellow-200/60 font-sans">
                <Award className="w-3.5 h-3.5 text-yellow-600" /> Founder's Perspective
              </span>
              
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-snug">
                Art That Transforms Spaces Into Living Narratives
              </h2>

              <p className="text-base sm:text-lg text-gray-600 leading-relaxed font-sans font-light">
                <strong className="text-gray-900 font-serif font-semibold">VIZAURA</strong> was founded by <strong className="text-gray-900 font-serif font-semibold">Jeet Mistry</strong>, a visual artist and designer born into an artistic family. With a background in Visual Communication and an enduring passion for spatial aesthetics, Jeet brings a distinctive vision that transforms blank walls into captivating centerpieces.
              </p>

              <p className="text-base sm:text-lg text-gray-600 leading-relaxed font-sans font-light">
                We believe that art in interiors should never be an afterthought. By seamlessly blending our clients' personal intent with visionary creative direction, each piece becomes a timeless reflection of individuality and elegance.
              </p>

              <div className="pt-2 flex flex-wrap justify-center md:justify-start gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-700 font-sans font-medium">
                  <CheckCircle2 className="w-4 h-4 text-yellow-600 shrink-0" /> Visual Communication Expertise
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700 font-sans font-medium">
                  <CheckCircle2 className="w-4 h-4 text-yellow-600 shrink-0" /> Architectural Harmony
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 3. Core Values / What Sets Us Apart */}
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12" data-aos="fade-up">
            <span className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-yellow-800 font-sans">
              Our Core Strengths
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mt-2">
              Why Discerning Clients Choose VIZAURA
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <motion.div
                key={i}
                data-aos="fade-up"
                data-aos-delay={i * 100}
                className="bg-white p-6 rounded-2xl border border-gray-200/80 hover:border-yellow-300 hover:shadow-xl hover:shadow-yellow-100/40 transition-all duration-300 flex flex-col items-start group"
              >
                <div className="p-3 rounded-xl bg-yellow-50/90 border border-yellow-100 group-hover:scale-110 transition-transform duration-300 mb-4">
                  {v.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 font-serif group-hover:text-yellow-700 transition-colors">
                  {v.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed font-sans font-light">
                  {v.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Get in Touch & Location Section */}
      <section className="py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            
            {/* Left Column - Contact Form */}
            <div className="lg:col-span-7 bg-white p-6 sm:p-10 rounded-3xl shadow-xl shadow-gray-200/60 border border-gray-200/80" data-aos="fade-right">
              <span className="text-xs font-semibold uppercase tracking-wider text-yellow-800 font-sans">
                Start A Conversation
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1 mb-3">
                Get In Touch With Our Studio
              </h2>
              <p className="text-sm sm:text-base text-gray-500 font-sans font-light mb-8">
                Ready to transform your residential or commercial space? Send us a message and our design team will connect with you.
              </p>

              {submitted ? (
                <div className="p-6 rounded-2xl bg-green-50 border border-green-200 text-center space-y-2 animate-fade-in">
                  <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
                  <h3 className="text-lg font-bold text-green-900 font-serif">Message Sent Successfully!</h3>
                  <p className="text-sm text-green-700 font-sans">
                    Thank you for reaching out. We will get back to you shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5 font-sans">
                      Your Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder="e.g. Rahul Sharma"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500/40 focus:border-yellow-600 transition-all text-sm font-sans"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5 font-sans">
                      Your Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="e.g. name@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500/40 focus:border-yellow-600 transition-all text-sm font-sans"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5 font-sans">
                      Project Details / Message
                    </label>
                    <textarea
                      name="message"
                      placeholder="Tell us about your space, dimensions, or style preferences..."
                      rows="4"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500/40 focus:border-yellow-600 transition-all text-sm font-sans resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-medium font-serif text-white bg-gray-900 hover:bg-yellow-600 transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-60"
                  >
                    {loading ? (
                      <span className="font-sans text-sm">Submitting...</span>
                    ) : (
                      <>
                        <span>Send Message</span>
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Right Column - Studio & Location Information */}
            <div className="lg:col-span-5 space-y-6" data-aos="fade-left">
              <div className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-200/80 shadow-xl shadow-gray-200/50 space-y-6">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-yellow-800 font-sans">
                    Studio & Headquarters
                  </span>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">
                    VIZAURA Art Studio
                  </h3>
                </div>

                <div className="space-y-4 pt-2 font-sans">
                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 rounded-lg bg-yellow-50 text-yellow-700 shrink-0 mt-0.5">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Address</p>
                      <p className="text-sm sm:text-base text-gray-800 font-serif mt-0.5">
                        Paldi, Bhattha, Ahmedabad, Gujarat 380007
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 rounded-lg bg-yellow-50 text-yellow-700 shrink-0 mt-0.5">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Direct Call / WhatsApp</p>
                      <a 
                        href="tel:+919016663115" 
                        className="text-sm sm:text-base text-gray-800 hover:text-yellow-600 transition-colors font-serif mt-0.5 block"
                      >
                        +91 9016663115 / +91 9429100173
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 rounded-lg bg-yellow-50 text-yellow-700 shrink-0 mt-0.5">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Email Enquiries</p>
                      <a 
                        href="mailto:info.vizaura@gmail.com" 
                        className="text-sm sm:text-base text-gray-800 hover:text-yellow-600 transition-colors font-serif mt-0.5 block"
                      >
                        info.vizaura@gmail.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-yellow-50/70 border border-yellow-100 text-xs sm:text-sm text-yellow-900 font-sans leading-relaxed">
                  <strong>Consultations:</strong> Studio visits and architectural project reviews available by appointment.
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;